// Desktop Mode (CR #133) — a TRIAL feature flag.
//
// It is OFF by default and every consumer reads it through here, because the
// request explicitly asks for a way to "go back to current state at the time of
// writing": with the flag off nothing in this codebase behaves differently.
//
// The flag is deliberately local (localStorage, per browser) rather than a
// server setting — it's a per-person UI preference during the trial, and it has
// to be readable synchronously during the first render so the app never flashes
// the wrong shell.
import { useEffect, useState } from 'react';

const LS_KEY = 'desktop_mode_v1';
const EVT = 'desktop-mode-changed';

/** Desktop surface + windowed navigation only make sense on a real pointer/size. */
const MD_BREAKPOINT = 768;

export const isDesktopModeEnabled = (): boolean => {
  try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
};

export const setDesktopModeEnabled = (on: boolean) => {
  try { localStorage.setItem(LS_KEY, on ? '1' : '0'); } catch { /* private mode */ }
  window.dispatchEvent(new Event(EVT));
};

/** Raw preference — use for the settings toggle itself. */
export function useDesktopModeSetting(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(isDesktopModeEnabled);
  useEffect(() => {
    const h = () => setOn(isDesktopModeEnabled());
    window.addEventListener(EVT, h);
    window.addEventListener('storage', h);
    return () => { window.removeEventListener(EVT, h); window.removeEventListener('storage', h); };
  }, []);
  return [on, setDesktopModeEnabled];
}

/** Effective mode — the preference AND a desktop-sized viewport. Phones keep
 *  their existing drawer/tab-bar UX untouched (they have their own window bar). */
export function useDesktopMode(): boolean {
  const [on, setOn] = useState(() => isDesktopModeEnabled() && window.innerWidth >= MD_BREAKPOINT);
  useEffect(() => {
    const h = () => setOn(isDesktopModeEnabled() && window.innerWidth >= MD_BREAKPOINT);
    window.addEventListener(EVT, h);
    window.addEventListener('storage', h);
    window.addEventListener('resize', h);
    return () => {
      window.removeEventListener(EVT, h);
      window.removeEventListener('storage', h);
      window.removeEventListener('resize', h);
    };
  }, []);
  return on;
}
